import { Link, createFileRoute } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';

import { PromoCounter } from '../components/PromoCounter';

export const Route = createFileRoute('/cennik')({
  component: PricingPage,
});

function PricingPage() {
  return (
    <>
      {/* Header */}
      <section className="px-6 pt-16 pb-12 text-center">
        <h1 className="font-display text-4xl font-semibold tracking-tight text-text md:text-5xl">
          99 zł raz — wiele miesięcy działania
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-text-muted">
          Im wcześniej dołączysz, tym taniej. Cena 99 zł zawsze, ale pokrywa
          różną liczbę miesięcy w zależności od pakietu. Po wyczerpaniu
          pakietów — standardowo 99 zł/mies.
        </p>

        {/* Live promo counter — shows tier ladder + progress */}
        <div className="mx-auto mt-8 max-w-lg text-left">
          <PromoCounter variant="card" />
        </div>
      </section>

      {/* Pricing cards — one per active tier */}
      <section className="px-6 pb-16">
        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-4">
          <PriceCard
            label="Pakiet założycielski"
            price="99 zł"
            sub="za pierwsze 12 mies."
            note="Tylko 10 miejsc. Oszczędzasz 1089 zł."
            ctaTo="/start"
            ctaLabel="Zajmij miejsce →"
            features={STARTER_FEATURES}
            highlight
          />
          <PriceCard
            label="Wczesny ptaszek"
            price="99 zł"
            sub="za pierwsze 6 mies."
            note="Miejsca 11-100. Oszczędzasz 495 zł."
            ctaTo="/start"
            ctaLabel="Zajmij miejsce"
            features={STARTER_FEATURES}
          />
          <PriceCard
            label="Promocja startowa"
            price="99 zł"
            sub="za pierwsze 3 mies."
            note="Miejsca 101-1000. Oszczędzasz 198 zł."
            ctaTo="/start"
            ctaLabel="Zajmij miejsce"
            features={STARTER_FEATURES}
          />
          <PriceCard
            label="Plan standardowy"
            price="99 zł"
            sub="/ mies. + VAT"
            note="Po wyczerpaniu pakietów. Albo 999 zł/rok."
            ctaTo="/start"
            ctaLabel="Załóż konto"
            features={STARTER_FEATURES}
          />
        </div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-text-subtle">
          Pakiet, w którym wylądujesz, jest decydowany w momencie potwierdzenia
          rejestracji. Płatność jednorazowa — bez automatycznych odnowień w
          okresie promocyjnym. Plan standardowy odnawia się miesięcznie.
        </p>
      </section>

      {/* What's included */}
      <section className="px-6 py-16 bg-surface/60 border-y border-border">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-text">
            Co dostajesz w planie Pozyskaj
          </h2>
          <div className="mt-10 grid gap-x-6 gap-y-3 md:grid-cols-2">
            {INCLUDED.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-md bg-bg p-3 ring-1 ring-border">
                <Check className="mt-0.5 size-4 text-success" aria-hidden="true" />
                <span className="text-sm text-text">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-text">
            Dodatki — kupisz, kiedy są potrzebne
          </h2>
          <p className="mt-3 text-center text-text-muted">
            Sprzedajemy à la carte. Bez gating, bez ukrytych płatności.
          </p>

          <ul className="mt-10 grid gap-4 md:grid-cols-2">
            {ADDONS.map((a) => (
              <li
                key={a.title}
                className="rounded-2xl bg-surface p-5 ring-1 ring-border"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-display text-base font-semibold text-text">{a.title}</p>
                  <p className="font-display text-base font-semibold text-accent tabular-nums">{a.price}</p>
                </div>
                <p className="mt-2 text-sm text-text-muted">{a.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Comparison */}
      <section className="px-6 py-16 bg-surface/60 border-t border-border">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-text">
            Porównanie z konkurencją
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-text-subtle">
                <tr>
                  <th className="px-4 py-3 font-semibold">Cecha</th>
                  <th className="px-4 py-3 font-semibold">ZnanyLekarz Starter</th>
                  <th className="px-4 py-3 font-semibold">MyDr Optymalny</th>
                  <th className="px-4 py-3 font-semibold bg-accent/5 text-accent">Pozyskaj</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg">
                <CompareRow label="Cena / mies." znl="399 zł" mydr="129 zł" ours="99 zł" oursWin />
                <CompareRow label="Prowizja za wizytę" znl="26-32 zł" mydr="1.49-2.99%" ours="0 zł" oursWin />
                <CompareRow label="Limit wizyt / mies." znl="bez limitu" mydr="10 (Start) / bez limitu" ours="bez limitu" />
                <CompareRow label="Strona kliniki (nie tylko profil)" znl={false} mydr={false} ours={true} />
                <CompareRow label="Strony per zabieg z SEO" znl={false} mydr={false} ours={true} />
                <CompareRow label="Content auto-generowany" znl={false} mydr={false} ours={true} />
                <CompareRow label="Rezerwacja 24/7" znl={true} mydr={true} ours={true} />
                <CompareRow label="SMS przypomnienia" znl="VIP (699 zł)" mydr={true} ours={true} />
                <CompareRow label="Drip kampanie SMS" znl="VIP (699 zł)" mydr={false} ours={true} />
                <CompareRow label="Wideokonsultacje" znl="Plus (499 zł)" mydr="Pro (339 zł)" ours="add-on" />
                <CompareRow label="EDM / dokumentacja medyczna" znl="Plus (499 zł)" mydr={true} ours="add-on" />
                <CompareRow label="Multi-lokalizacja" znl="Plus (499 zł)" mydr="placówka (299 zł)" ours={true} />
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-text-subtle text-center">
            Dane porównawcze z publicznych cenników konkurencji (czerwiec 2026). Wyniki mogą się różnić w czasie.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl rounded-3xl bg-text px-8 py-16 text-center text-bg">
          <h2 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Załóż konto i zobacz swoją stronę
          </h2>
          <p className="mt-3 text-base text-bg/80">
            Bez karty kredytowej. Płacisz dopiero kiedy zaakceptujesz wygenerowaną stronę.
          </p>
          <Link
            to="/start"
            className="mt-8 inline-flex rounded-full bg-bg px-6 py-3 text-base font-medium text-text hover:bg-bg/90"
          >
            Zacznij teraz →
          </Link>
        </div>
      </section>
    </>
  );
}

const STARTER_FEATURES = [
  'Strona kliniki na *.pozyskajpacjenta.pl (+ własna domena)',
  'Formularz kontaktowy + lead audit RODO',
  'Kalendarz rezerwacji online — bez prowizji',
  'SMS przypomnienia + drip kampanie',
  'Auto-treść SEO + content marketplace add-ons',
  'RBAC, panel admin, polityka prywatności per-tenant',
];

const INCLUDED = [
  'Strona kliniki generowana przez AI (multi-page: home + zabiegi + kontakt + RODO)',
  'Domena *.pozyskajpacjenta.pl + opcjonalnie własna domena',
  'Cloudflare SSL na własnej domenie (automatycznie)',
  'Kalendarz rezerwacji online 24/7 — bez prowizji od wizyt',
  'Wielo-doktorski widok koordynacji (drag-and-drop)',
  'SMS przypomnienia T-24h (transactional)',
  'Drip kampanie SMS — potwierdzenia, recall, podziękowania',
  'Automatyczne prośby o opinię na Google',
  'Lead form + audit RODO',
  'Polityka prywatności generowana per-tenant',
  'Integracja z Google Business Profile (synchronizacja)',
  'Integracja z znanylekarz / Booksy (osobno per lekarz)',
  'iCal export per lekarz (Apple/Google/Outlook)',
  '6 stylów wizualnych do wyboru (571 themes pod spodem)',
  'Lista wszystkich opcji w panelu admin',
  'RBAC: właściciel, menedżer, recepcja, tylko-podgląd',
  'Wsparcie e-mail',
];

const ADDONS = [
  { title: 'Blog miesięczny — 4 wpisy', price: '799 zł / mies.', description: '4 artykuły SEO, 800-1200 słów, 2 linki wewnętrzne, briefing z Search Console.' },
  { title: 'Local SEO — 6 stron miast', price: '999 zł / mies.', description: '6 podstron typu „<usługa> w <miasto>" z unikalnym tekstem, mapą i FAQ specyficznym dla lokalizacji.' },
  { title: 'Backlinki DR40+', price: '1999 zł / kwartał', description: '5 dofollow backlinków z domen DR40+, manualny outreach, anchor uzgadniany.' },
  { title: 'Backlinki niche DR30+', price: '2499 zł / mies.', description: '10 backlinków/mies z polskich domen tematycznych. Mix editorial + kontekstowych.' },
  { title: 'Cytaty eksperckie', price: '1499 zł / kwartał', description: '3 publikacje z wypowiedzią Twojego lekarza + dofollow link. PR managed.' },
  { title: 'Wideokonsultacje (Daily.co)', price: '~7 gr / min', description: 'Patient-facing video room. Per-visit cost-pass-through. Brak abonamentu.' },
];

function PriceCard({
  label,
  price,
  sub,
  note,
  ctaTo,
  ctaLabel,
  highlight,
  features,
}: {
  label: string;
  price: string;
  sub: string;
  note: string;
  ctaTo: string;
  ctaLabel: string;
  highlight?: boolean;
  features?: string[];
}) {
  return (
    <div
      className={
        highlight
          ? 'rounded-2xl bg-text p-8 text-bg ring-2 ring-text shadow-xl'
          : 'rounded-2xl bg-bg p-8 ring-1 ring-border'
      }
    >
      <p
        className={
          highlight
            ? 'text-xs font-semibold uppercase tracking-wide text-bg/70'
            : 'text-xs font-semibold uppercase tracking-wide text-text-subtle'
        }
      >
        {label}
        {highlight ? <span className="ml-2 rounded-full bg-accent/30 px-2 py-0.5 text-[10px] text-bg">2 mies. gratis</span> : null}
      </p>
      <p className="mt-3 font-display text-5xl font-semibold tracking-tight">{price}</p>
      <p className={highlight ? 'mt-1 text-sm text-bg/70' : 'mt-1 text-sm text-text-muted'}>{sub}</p>
      <p className={highlight ? 'mt-4 text-xs text-bg/60' : 'mt-4 text-xs text-text-subtle'}>{note}</p>
      {features && (
        <ul className={highlight ? 'mt-6 space-y-2 text-sm text-bg/80' : 'mt-6 space-y-2 text-sm text-text-muted'}>
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className={highlight ? 'mt-0.5 size-4 shrink-0 text-bg' : 'mt-0.5 size-4 shrink-0 text-success'} aria-hidden="true" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      <Link
        to={ctaTo}
        className={
          highlight
            ? 'mt-8 inline-flex w-full justify-center rounded-full bg-bg px-6 py-3 font-medium text-text hover:bg-bg/90'
            : 'mt-8 inline-flex w-full justify-center rounded-full bg-text px-6 py-3 font-medium text-bg hover:bg-text/90'
        }
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

function CompareRow({
  label,
  znl,
  mydr,
  ours,
  oursWin,
}: {
  label: string;
  znl: string | boolean;
  mydr: string | boolean;
  ours: string | boolean;
  oursWin?: boolean;
}) {
  const cellOf = (v: string | boolean) =>
    typeof v === 'boolean' ? (
      v ? (
        <Check className="size-4 text-success" aria-hidden="true" />
      ) : (
        <X className="size-4 text-text-subtle" aria-hidden="true" />
      )
    ) : (
      <span>{v}</span>
    );
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-text">{label}</td>
      <td className="px-4 py-3 text-text-muted">{cellOf(znl)}</td>
      <td className="px-4 py-3 text-text-muted">{cellOf(mydr)}</td>
      <td className={oursWin ? 'px-4 py-3 font-semibold text-accent' : 'px-4 py-3 text-accent'}>
        {cellOf(ours)}
      </td>
    </tr>
  );
}
